import React from 'react'
import { withFirebase } from '../Fb'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import IconButton from '@mui/material/IconButton'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import FalconModal from '../Inputs/FalconModal'
import {
  getOpeningHoursRuleSchema,
  getOpeningHoursRuleUiSchema,
} from '../Inputs/dataschemas'
import {
  flattenRulesToDefaults,
  buildDefaultRuleFromLegacy,
  WEEKDAY_LABELS_FI,
} from '../utils/openingHoursUtils'

const ALWAYS_LABEL = '— aina voimassa —'

const formatDateRange = (rule) => {
  if (!rule) return ALWAYS_LABEL
  if (!rule.validstart && !rule.validend) return ALWAYS_LABEL
  const s = rule.validstart || '…'
  const e = rule.validend || '…'
  return `${s} – ${e}`
}

const formatSlotsSummary = (rule) => {
  if (!rule) return '—'
  if (rule.closed === true) {
    const slots = Array.isArray(rule.slots) ? rule.slots : []
    if (slots.length === 0) return 'Suljettu (kaikki päivät)'
    const dayList = slots
      .map((slot) => {
        const days = (slot.days || [])
          .slice()
          .sort((a, b) => a - b)
          .map((d) => WEEKDAY_LABELS_FI[d - 1])
          .join(', ')
        return days || '—'
      })
      .join(' · ')
    return `Suljettu — ${dayList}`
  }
  if (!Array.isArray(rule.slots) || rule.slots.length === 0) return '—'
  return rule.slots
    .map((slot) => {
      const days = (slot.days || [])
        .slice()
        .sort((a, b) => a - b)
        .map((d) => WEEKDAY_LABELS_FI[d - 1])
        .join(', ')
      return `${days || '—'} ${slot.starttime || '?'}–${slot.endtime || '?'}`
    })
    .join(' · ')
}

class OpeningTimes extends React.Component {
  state = {
    loading: true,
    rules: [],
    legacyDefaults: [],
    modalOpen: false,
    selectedRule: null,
  }

  componentDidMount() {
    this.unsubscribe = this.props.firebase
      .getOpeningTimes()
      .onSnapshot((doc) => {
        if (!doc.exists) {
          this.setState({ loading: false, rules: [], legacyDefaults: [] })
          return
        }
        const data = doc.data() || {}
        this.setState({
          loading: false,
          rules: Array.isArray(data.rules) ? data.rules : [],
          legacyDefaults: Array.isArray(data.defaults) ? data.defaults : [],
        })
      })
  }

  componentWillUnmount() {
    if (this.unsubscribe) this.unsubscribe()
  }

  toggleModal = () => {
    this.setState((s) => ({ modalOpen: !s.modalOpen }))
  }

  handleAdd = () => {
    this.setState({
      selectedRule: {
        active: true,
        priority: 100,
        validstart: '',
        validend: '',
        closed: false,
        message: '',
        slots: [{ days: [1, 2, 3, 4, 5], starttime: '08:00', endtime: '22:00' }],
      },
      modalOpen: true,
    })
  }

  handleEdit = (rule) => {
    this.setState({ selectedRule: { ...rule }, modalOpen: true })
  }

  handleDelete = async (rule) => {
    if (!rule || !rule.id) return
    const confirmMsg = `Poista aukioloaika "${rule.name || rule.id}"?`
    if (!window.confirm(confirmMsg)) return
    await this.props.firebase.deleteOpeningHoursRule(rule.id, { flattenRulesToDefaults })
  }

  handleMigrate = async () => {
    const seed = buildDefaultRuleFromLegacy(this.state.legacyDefaults)
    if (!seed) return
    await this.props.firebase.saveOpeningHoursRule(seed, { flattenRulesToDefaults })
  }

  saveRule = async (rule) => {
    return this.props.firebase.saveOpeningHoursRule(rule, { flattenRulesToDefaults })
  }

  renderModal = () => {
    if (!this.state.modalOpen) return null
    const isEdit = this.state.selectedRule && this.state.selectedRule.id
    return (
      <FalconModal
        onUpdate={this.saveRule}
        onCreate={this.saveRule}
        toggle={this.toggleModal}
        resource={this.state.selectedRule}
        isOpen={this.state.modalOpen}
        uiSchema={getOpeningHoursRuleUiSchema()}
        dataSchema={getOpeningHoursRuleSchema()}
        title={isEdit ? 'Muokkaa aukioloaikaa' : 'Lisää aukioloaika'}
      />
    )
  }

  renderEmpty = () => {
    const hasLegacy = this.state.legacyDefaults && this.state.legacyDefaults.length > 0
    return (
      <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
        <Typography variant='body1' sx={{ mb: 2 }}>
          Aukioloaikoja ei ole vielä määritetty.
        </Typography>
        {hasLegacy ? (
          <>
            <Typography variant='body2' sx={{ mb: 2 }}>
              Aiemmat viikoittaiset aukioloajat löytyvät tallessa. Tuo ne uudeksi
              "Oletus"-säännöksi yhdellä klikkauksella, ja muokkaa siitä eteenpäin.
            </Typography>
            <Button variant='outlined' onClick={this.handleMigrate} sx={{ mr: 1 }}>
              Tuo aiemmat aukioloajat
            </Button>
            <Button variant='contained' onClick={this.handleAdd} startIcon={<AddIcon />}>
              Lisää uusi sääntö
            </Button>
          </>
        ) : (
          <Button variant='contained' onClick={this.handleAdd} startIcon={<AddIcon />}>
            Lisää ensimmäinen aukioloaika
          </Button>
        )}
      </Box>
    )
  }

  renderTable = () => {
    const sorted = this.state.rules
      .slice()
      .sort((a, b) => (Number(a.priority) || 0) - (Number(b.priority) || 0))
    return (
      <Table size='small' sx={{ '& td, & th': { fontSize: 13 } }}>
        <TableHead>
          <TableRow>
            <TableCell>Nimi</TableCell>
            <TableCell>Voimassaolo</TableCell>
            <TableCell>Aukioloajat</TableCell>
            <TableCell sx={{ textAlign: 'right' }}>Prioriteetti</TableCell>
            <TableCell sx={{ textAlign: 'center' }}>Käytössä</TableCell>
            <TableCell sx={{ width: 120 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((rule) => (
            <TableRow key={rule.id} hover>
              <TableCell>{rule.name || '(nimetön)'}</TableCell>
              <TableCell sx={{ color: 'text.secondary' }}>{formatDateRange(rule)}</TableCell>
              <TableCell sx={{ color: 'text.secondary' }}>
                <Box>{formatSlotsSummary(rule)}</Box>
                {rule.closed === true && rule.message && (
                  <Box sx={{ mt: 0.5, fontStyle: 'italic', color: 'text.disabled', fontSize: 12 }}>
                    Viesti: {rule.message}
                  </Box>
                )}
              </TableCell>
              <TableCell sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {rule.priority ?? '—'}
              </TableCell>
              <TableCell sx={{ textAlign: 'center', color: rule.active ? '#28a745' : '#dc3545' }}>
                {rule.active ? 'Kyllä' : 'Ei'}
              </TableCell>
              <TableCell sx={{ textAlign: 'right' }}>
                <IconButton size='small' onClick={() => this.handleEdit(rule)} aria-label='Muokkaa'>
                  <EditIcon fontSize='small' />
                </IconButton>
                <IconButton size='small' onClick={() => this.handleDelete(rule)} aria-label='Poista'>
                  <DeleteIcon fontSize='small' />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    )
  }

  render() {
    if (this.state.loading) {
      return (
        <Card sx={{ borderRadius: 2 }}>
          <CardContent>
            <Typography variant='body2'>Ladataan…</Typography>
          </CardContent>
        </Card>
      )
    }
    const hasRules = this.state.rules.length > 0
    return (
      <>
        <Card sx={{ borderRadius: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant='h6'>Aukioloajat</Typography>
              {hasRules && (
                <Button variant='contained' size='small' startIcon={<AddIcon />} onClick={this.handleAdd}>
                  Lisää aukioloaika
                </Button>
              )}
            </Box>
            <Typography variant='body2' sx={{ color: 'text.secondary', mb: 2 }}>
              Säännöt sovelletaan prioriteettijärjestyksessä — pienempi numero on
              korkeampi prioriteetti. Päivämääräväli rajaa, milloin sääntö on
              voimassa; jätä päivät tyhjiksi jos sääntö pätee aina.
            </Typography>
            {hasRules ? this.renderTable() : this.renderEmpty()}
          </CardContent>
        </Card>
        {this.renderModal()}
      </>
    )
  }
}

export default withFirebase(OpeningTimes)
